/**
 * What the automated outreach chain has already done by email on one VoE,
 * for the confirm shown before a recruiter sends a manual email.
 *
 * A manual touch before the employer answers turns an autonomous response
 * into an assisted one, so when the chain has already emailed (or is about
 * to), the Send action asks first. Said only as far as the action rows make
 * certain: the dates that went out and whether another one is queued. No
 * timings, because a queued step can still be rescheduled or cancelled.
 */
import { useCallback } from 'react'

import { useApolloClient, useQuery } from '@apollo/client'

import { VoEOutreachType } from 'src/graphql/types/verificationOfEmployments'
import { GET_VOE_ACTIONS } from 'src/graphql/voe.graphql'
import { formatDateCustom } from 'src/lib/formatters'

export interface VoEActionLike {
  type?: string | null
  status?: string | null
  configStepOrder?: number | null
  scheduledAt?: string | Date | null
  executedAt?: string | Date | null
}

export interface AutomatedEmailSummary {
  /** When each automated email went out, oldest first. */
  completedDates: Date[]
  /** True when an automated email is queued and has not run yet. */
  hasScheduled: boolean
}

export interface AutomatedEmailConfirm {
  shouldConfirm: boolean
  description: string
  automatedSentCount: number
  hasScheduled: boolean
}

const isAutomatedEmail = (action: VoEActionLike) =>
  action.configStepOrder != null && action.type === VoEOutreachType.EMAIL

export const summarizeAutomatedEmails = (
  actions: VoEActionLike[] | null | undefined
): AutomatedEmailSummary => {
  const automatedEmails = (actions ?? []).filter(isAutomatedEmail)
  const completedDates = automatedEmails
    .filter((a) => a.status === 'COMPLETED')
    .map((a) => a.executedAt ?? a.scheduledAt)
    .filter((d): d is string | Date => d != null)
    .map((d) => new Date(d))
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime())
  return {
    completedDates,
    hasScheduled: automatedEmails.some((a) => a.status === 'SCHEDULED'),
  }
}

/** "25 Sep", "22 Sep and 25 Sep", "18 Sep, 22 Sep and 25 Sep". */
const joinDates = (dates: Date[]): string => {
  const labels = Array.from(
    new Set(dates.map((d) => formatDateCustom(d, 'D MMM')))
  )
  if (labels.length <= 1) return labels[0] ?? ''
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

export const describeAutomatedEmails = (
  summary: AutomatedEmailSummary,
  email: string | null | undefined
): AutomatedEmailConfirm => {
  const { completedDates, hasScheduled } = summary
  const automatedSentCount = completedDates.length
  const recipient = email?.trim() || 'this employer'

  let description = ''
  if (automatedSentCount > 0) {
    description = `Automation emailed ${recipient} on ${joinDates(
      completedDates
    )}${hasScheduled ? ' and has another scheduled' : ''}. Send one anyway?`
  } else if (hasScheduled) {
    const to = email?.trim() ? ` to ${email.trim()}` : ''
    description = `Automation has an email${to} scheduled. Send one anyway?`
  }

  return {
    shouldConfirm: automatedSentCount > 0 || hasScheduled,
    description,
    automatedSentCount,
    hasScheduled,
  }
}

/**
 * The automated email actions on a VoE. Shares the Apollo cache entry with
 * the audit trail's outreach rows, so the drawer's refresh updates both.
 * Skipped (empty summary) when `skip` is set, e.g. automation is off.
 */
export const useAutomatedEmailActions = (
  voeId: string | null | undefined,
  { skip = false }: { skip?: boolean } = {}
): AutomatedEmailSummary => {
  const { data } = useQuery(GET_VOE_ACTIONS, {
    variables: { voeId },
    skip: skip || !voeId,
    fetchPolicy: 'cache-and-network',
  })
  return summarizeAutomatedEmails(skip ? [] : data?.voeActions)
}

/**
 * Fetches the automated email actions from the server, bypassing the cache,
 * for a decision that must not run on a stale copy. The result also refreshes
 * the cache entry the drawer's audit trail reads.
 */
export const useFetchLatestAutomatedEmails = (
  voeId: string | null | undefined
): (() => Promise<AutomatedEmailSummary>) => {
  const client = useApolloClient()
  return useCallback(async () => {
    if (!voeId) return summarizeAutomatedEmails([])
    const { data } = await client.query({
      query: GET_VOE_ACTIONS,
      variables: { voeId },
      fetchPolicy: 'network-only',
    })
    return summarizeAutomatedEmails(data?.voeActions)
  }, [client, voeId])
}
